import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useState } from 'react';
import {
  Create,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { dataProvider } from 'src/providers/data-provider';

interface FormField {
  id: string;
  originalKey: string;
  displayName: string;
  value?: any;
  order: number;
}

interface FormTemplateCreateContentProps {
  fields: FormField[];
  setFields: (fields: FormField[]) => void;
  selectedImage: any;
  setSelectedImage: any;
}

const FormTemplateCreateContent: React.FC<FormTemplateCreateContentProps> = ({
  fields,
  setFields,
  selectedImage,
  setSelectedImage,
}) => {
  const translate = useTranslate();
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const notify = useNotify();
  const [imagePreview, setImagePreview] = useState<any>(null);

  const handleFieldNameChange = (index: number, newName: string) => {
    const updatedFields = [...fields];
    updatedFields[index] = {
      ...updatedFields[index],
      displayName: newName,
    };
    setFields(updatedFields);
  };

  const handleAddField = () => {
    if (!newFieldName) {
      notify(translate('simple.field_name_is_required'), { type: 'error' });
      return;
    }

    const timestamp = Date.now();
    const newId = `draggable-new-${timestamp}`;
    const newField = {
      id: newId,
      originalKey: newFieldName,
      displayName: newFieldName,
      order: fields.length,
    };

    setFields([...fields, newField]);
    setNewFieldName('');
    setShowFieldDialog(false);
  };

  const handleRemoveField = (index: number) => {
    const updatedFields = fields.filter((_, i) => i !== index);
    setFields(updatedFields);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const reorderedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    setFields(reorderedItems);
  };

  const handleImageChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setSelectedImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Box display="flex" gap={2} width="100%">
        <Box flex={1}>
          <Box
            sx={{
              width: '400px',
              height: '192px',
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.300',
              mb: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {imagePreview ? (
              <Box sx={{ position: 'relative', height: '100%' }}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Template preview"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <label>
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        color: 'primary.main',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                    >
                      <CameraAltIcon />
                      <Typography>{translate('simple.reselect')}</Typography>
                      <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                    </Box>
                  </label>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CameraAltIcon
                  sx={{
                    color: 'grey.400',
                    fontSize: 48,
                    mb: 1,
                  }}
                />
                <label>
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'common.white',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    <Typography>{translate('simple.select_a_photo')}</Typography>
                    <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                  </Box>
                </label>
              </Box>
            )}
          </Box>

          <TextInput
            source="name"
            validate={required()}
            fullWidth
            label={translate('resources.form_template.fields.name')}
          />

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {translate('resources.form_template.fields.form_fields')}
          </Typography>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-fields">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} style={{ minHeight: '60px' }}>
                  {fields.map((field, index) => (
                    <Draggable key={field.id} draggableId={field.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            marginBottom: '8px',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            background: snapshot.isDragging ? '#f0f0f0' : 'white',
                          }}
                        >
                          <Box display="flex" gap={1} alignItems="center">
                            <div {...provided.dragHandleProps}>
                              <Box className="text-gray-400">≡</Box>
                            </div>
                            <Box flexGrow={1}>
                              <TextInput
                                source={`fields.${index}.name`}
                                label={field.originalKey}
                                defaultValue={field.displayName}
                                onChange={(e) => handleFieldNameChange(index, e.target.value)}
                                fullWidth
                              />
                            </Box>
                            <Button onClick={() => handleRemoveField(index)} color="error" size="small">
                              {translate('ra.action.delete')}
                            </Button>
                          </Box>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Button variant="contained" onClick={() => setShowFieldDialog(true)} sx={{ mt: 2 }}>
            {translate('simple.add_new_field')}
          </Button>
        </Box>
      </Box>

      <Dialog open={showFieldDialog} onClose={() => setShowFieldDialog(false)}>
        <DialogTitle>{translate('simple.add_new_field')}</DialogTitle>
        <DialogContent>
          <TextInput
            source="newFieldName"
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
            label={translate('simple.new_field_name')}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFieldDialog(false)}>{translate('ra.action.cancel')}</Button>
          <Button onClick={handleAddField} color="primary">
            {translate('ra.action.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FormTemplateCreate = () => {
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const uploadTemplateImage = async (file: any) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const authParams = localStorage?.getItem('auth') || '';
      const { access_token } = JSON.parse(authParams);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/form/upload-template-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const data = await response.json();
      return data.image_key;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);

      let imageKey = null;
      if (selectedImage) {
        imageKey = await uploadTemplateImage(selectedImage);
      }

      if (!imageKey) {
        notify('simple.image_is_required', { type: 'error' });
        return;
      }

      const formattedFields = fields.map((field, index) => ({
        field_name: field.displayName,
        field_name_original: field.originalKey,
        field_type: 'text',
        order: index + 1,
      }));

      const gqlQuery = imageKey
        ? `
          mutation CreateFormTemplate($name: String!, $fields: [form_field_insert_input!]!, $imageKey: String) {
            insert_form_template_one(object: {
              name: $name,
              description: $name,
              form_fields: {
                data: $fields
              },
              image_key: $imageKey
            }) {
              id
              
            }
          }
        `
        : `
          mutation CreateFormTemplate($name: String!, $fields: [form_field_insert_input!]!) {
            insert_form_template_one(object: {
              name: $name,
              description: $name,
              form_fields: {
                data: $fields
              }
            }) {
              id
              
            }
          }
        `;

      const variables = imageKey
        ? {
            name: formData.name,
            fields: formattedFields,
            imageKey: imageKey,
          }
        : {
            name: formData.name,
            fields: formattedFields,
          };

      const response = await dataProvider.create('form_template', {
        data: {
          name: formData.name,
          imageKey: imageKey,
          fields: formattedFields,
        },
        meta: {
          gqlQuery,
          variables,
        },
      });

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      notify(translate('resources.form_template.notification.created_success'), { type: 'success' });
      redirect('list', 'form_template');
    } catch (error) {
      console.error('Create error:', error);
      notify(translate('resources.form_template.notification.created_error'), { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Create>
      <SimpleForm
        onSubmit={handleSubmit}
        toolbar={
          <Toolbar>
            <SaveButton disabled={isSubmitting} />
          </Toolbar>
        }
      >
        <FormTemplateCreateContent
          fields={fields}
          setFields={setFields}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
        />
      </SimpleForm>
    </Create>
  );
};

export default FormTemplateCreate;
